import { create } from './store';
import { createLogger } from 'shared/logger';
import {
  ConstraintMode,
  ConstraintProfile,
  CreateConstraintProfileRequest,
  UpdateConstraintProfileRequest,
} from 'types/ConstraintProfileTypes';
import { ConstraintProfilesService } from 'shared/services/ConstraintProfilesService';
import constraintsStore from './constraintsStore';

const log = createLogger('ConstraintProfiles');

interface ConstraintProfilesState {
  /** The saved-profile catalog cached per mode. */
  profilesByMode: { builder: ConstraintProfile[]; rebalancing: ConstraintProfile[] };
  /** True once the session-startup loader has populated both buckets. */
  loaded: boolean;
  isLoading: boolean;
  saveDialogOpen: boolean;
  manageDialogOpen: boolean;
}

interface ConstraintProfilesActions {
  fetchProfiles: (mode: ConstraintMode) => Promise<void>;
  createProfile: (
    data: CreateConstraintProfileRequest,
  ) => Promise<ConstraintProfile>;
  updateProfile: (
    profileId: string,
    data: UpdateConstraintProfileRequest,
  ) => Promise<ConstraintProfile>;
  deleteProfile: (profileId: string) => Promise<void>;
  setSaveDialogOpen: (open: boolean) => void;
  setManageDialogOpen: (open: boolean) => void;
  /** Mark the session-startup load complete. */
  setLoaded: (loaded: boolean) => void;
  /** Clear all session-scoped state on logout. */
  clearSession: () => void;
}

const emptyByMode = (): { builder: ConstraintProfile[]; rebalancing: ConstraintProfile[] } => ({
  builder: [],
  rebalancing: [],
});

const initialState: ConstraintProfilesState = {
  profilesByMode: emptyByMode(),
  loaded: false,
  isLoading: false,
  saveDialogOpen: false,
  manageDialogOpen: false,
};

const constraintProfilesStore = create<
  ConstraintProfilesState & ConstraintProfilesActions
>()((set, get) => ({
  ...initialState,

  fetchProfiles: async (mode) => {
    set({ isLoading: true });
    try {
      const profiles = await ConstraintProfilesService.listProfiles(mode);
      set({
        profilesByMode: { ...get().profilesByMode, [mode]: profiles },
        isLoading: false,
      });
    } catch (error) {
      log.error('fetch constraint profiles failed', { error });
      set({ isLoading: false });
    }
  },

  createProfile: async (data) => {
    const created = await ConstraintProfilesService.createProfile(data);
    const bucket = get().profilesByMode[data.mode];
    const next = data.is_default
      ? bucket.map((p) => (p.is_default ? { ...p, is_default: false } : p))
      : bucket.slice();
    set({
      profilesByMode: { ...get().profilesByMode, [data.mode]: [...next, created] },
    });
    return created;
  },

  updateProfile: async (profileId, data) => {
    const updated = await ConstraintProfilesService.updateProfile(profileId, data);
    const bucket = get().profilesByMode[updated.mode];
    const nextBucket = bucket.map((p) => {
      if (p.id === profileId) return updated;
      // Promoting another profile to default clears the prior default of the same mode.
      if (data.is_default && p.is_default) return { ...p, is_default: false };
      return p;
    });
    set({ profilesByMode: { ...get().profilesByMode, [updated.mode]: nextBucket } });

    // Mirror the renamed name into the applied-profile snapshot if it's the
    // currently applied one. Without this, the dropdown trigger label and any
    // subsequent metadata write (buildConstraintsSyncPayload, Invia handler)
    // would still carry the stale name until a page refresh. Only the name
    // is mirrored — `appliedProfile.document` is the frozen baseline for the
    // `(modificato)` diff and must not silently change here. Document-level
    // re-baselines after Salva go through `onProfileSaved`.
    const applied = constraintsStore.getState().appliedProfile;
    if (applied && applied.id === profileId && applied.name !== updated.name) {
      constraintsStore.setState({
        appliedProfile: { ...applied, name: updated.name },
      });
    }
    return updated;
  },

  deleteProfile: async (profileId) => {
    await ConstraintProfilesService.deleteProfile(profileId);
    const byMode = get().profilesByMode;
    set({
      profilesByMode: {
        builder: byMode.builder.filter((p) => p.id !== profileId),
        rebalancing: byMode.rebalancing.filter((p) => p.id !== profileId),
      },
    });
  },

  setSaveDialogOpen: (open) => set({ saveDialogOpen: open }),
  setManageDialogOpen: (open) => set({ manageDialogOpen: open }),
  setLoaded: (loaded) => set({ loaded }),
  clearSession: () =>
    set({
      profilesByMode: emptyByMode(),
      loaded: false,
      isLoading: false,
      saveDialogOpen: false,
      manageDialogOpen: false,
    }),
}));

export default constraintProfilesStore;
